import axios from 'axios';

// Создаём экземпляр Axios с базовым URL и настройками по умолчанию
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) {
    throw new Error("REACT_APP_API_BASE_URL не задан в .env");
}

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 5000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Интерсептор для добавления токена в заголовки запросов
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("token");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Интерсептор для логирования ответов и ошибок (опционально)
axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/* ##########################
   ### Аутентификация ###
   ########################## */

// Регистрация пользователя
export const register = (userData) => axiosInstance.post('/auth/register', userData);

// Авторизация пользователя
export const login = (userData) => axiosInstance.post('/auth/login', userData);

/* ##########################
   ### Управление профилями ###
   ########################## */

// Добавление профиля
export const addProfile = (profileData) => axiosInstance.post('/profiles/', profileData);

// Получение всех профилей
export const getProfiles = () => axiosInstance.get('/profiles/');

// Удаление профиля по ID
export const deleteProfile = (id) => axiosInstance.delete(`/profiles/${id}`);

// Переупорядочивание профилей
export const reorderProfiles = (orderedIds) =>
    axiosInstance.put("/profiles/reorder", { ordered_ids: orderedIds });

// Получение данных конкретного профиля
export const getProfileData = (profileId) => axiosInstance.get(`/profiles/profile-data/${profileId}`);

/* ##########################
   ### Работа с аватарами ###
   ########################## */

// Проверка аватара
export const checkAvatar = async (avatarHash) => {
    try {
        const response = await axiosInstance.get(`/profiles/avatars/${avatarHash}`);
        if (response.data.exists) {
            return `${axiosInstance.defaults.baseURL}/static/photos/${avatarHash}.jpg`;
        }
        return `${axiosInstance.defaults.baseURL}/static/images.jpeg`;
    } catch (error) {
        console.error("Ошибка при проверке аватара:", error);
        return `${axiosInstance.defaults.baseURL}/static/images.jpeg`;
    }
};

/* ##########################
   ### Управление рассылкой ###
   ########################## */

// Запуск онлайн для профиля
export const startOnline = (profileId) =>
    axiosInstance.post(`/bot/start_online`, { profile_id: profileId })
        .then(response => {
            console.log("Профиль запущен:", response.data);
            return response.data;
        }).catch(error => {
            console.error("Ошибка при запуске профиля:", error.response?.data || error.message);
            throw error;
        });

// Остановка онлайн для профиля
export const stopOnline = (profileId) =>
    axiosInstance.post(`/bot/stop_online`, { profile_id: profileId })
        .then(response => {
            console.log("Профиль остановлен:", response.data);
            return response.data;
        }).catch(error => {
            console.error("Ошибка при остановке профиля:", error.response?.data || error.message);
            throw error;
        });

// Новый метод для получения статуса бота
export const getBotStatus = (profileId) =>
    axiosInstance.get(`/bot/status/${profileId}`)
        .then(response => response.data)
        .catch(error => {
            console.error("Ошибка при получении статуса бота:", error.response?.data || error.message);
            throw error;
        });

// Запуск задачи fetch_online_men_for_user для профиля
export const fetchMenOnline = (profileId) =>
    axiosInstance.post(`/bot/fetch_men_online`, { profile_id: profileId })
        .then(response => {
            console.log("Получение мужчин онлайн запущено:", response.data);
            return response.data;
        }).catch(error => {
            console.error("Ошибка при запуске получения мужчин онлайн:", error.response?.data || error.message);
            throw error;
        });

// Остановка получения мужчин онлайн (если нужно)
export const stopFetchMenOnline = (profileId) =>
    axiosInstance.post(`/bot/stop_fetch_men_online`, { profile_id: profileId })
        .then(response => {
            console.log("Получение мужчин онлайн остановлено:", response.data);
            return response.data;
        }).catch(error => {
            console.error("Ошибка при остановке получения мужчин онлайн:", error.response?.data || error.message);
            throw error;
        });

// Получение онлайн ID из Redis для профиля
export const getOnlineIds = (profileId) =>
    axiosInstance.get(`/bot/get-online-ids`, {
        params: {
            profile_id: profileId,
        },
    });

/* ##########################
   ### Работа с письмами ###
   ########################## */

// Добавление письма
export const addLetter = (profileId, content) => axiosInstance.post('/api/letters/add-letter', { profile_id: profileId, content });

// Получение старых писем
export const getOldLetters = (profileId) => axiosInstance.get(`/api/letters/old-letters/${profileId}`);

// Получение предварительного просмотра сообщения
export const getMessagePreview = (profileId) => axiosInstance.get(`/api/letters/get-message-preview/${profileId}`);

/* ##########################
   ### Работа с изображениями ###
   ########################## */

// Загрузка изображения
export const uploadImage = async (formData) => {
    try {
        const response = await axiosInstance.post('/api/images/upload-image', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response;
    } catch (error) {
        console.error("Ошибка при загрузке изображения:", error);
        throw error;
    }
};

/* ##########################
   ### Очистка данных ###
   ########################## */

// Очистка отправленных писем для профиля
export const clearSentLetters = (profileId) =>
    axiosInstance.delete(`/api/cleanup/profiles/${profileId}/sent_letters`);

// Очистка лимита профиля
export const clearProfileLimit = (profileId) =>
    axiosInstance.delete(`/api/cleanup/profiles/${profileId}/profile_limit`);

// Очистка хэша фотографии
export const clearPhotoHash = (profileId) =>
    axiosInstance.delete(`/api/cleanup/profiles/${profileId}/photo_hash`);

// Очистка всех данных профиля
export const clearAllData = (profileId) =>
    axiosInstance.delete(`/api/cleanup/profiles/${profileId}/all`);

// Очистка отправленных писем для всех профилей
export const clearAllSentLetters = () =>
    axiosInstance.delete(`/api/cleanup/profiles/sent_letters`);

// Очистка лимитов профилей для всех профилей
export const clearAllProfileLimits = () =>
    axiosInstance.delete(`/api/cleanup/profiles/profile_limits`);

// Очистка хэшей фотографий для всех профилей
export const clearAllPhotoHashes = () =>
    axiosInstance.delete(`/api/cleanup/profiles/photo_hashes`);

// Очистка всех данных для всех профилей
export const clearAllProfilesData = () =>
    axiosInstance.delete(`/api/cleanup/profiles/all`);

/* ##########################
   ### Работа со списком игнорируемых ###
   ########################## */

// Добавление пользователя в список игнорируемых
export const addToIgnore = (profileId, targetId) =>
    axiosInstance.post(`/profiles/${profileId}/ignore`, { target_id: targetId });

// Получение списка игнорируемых пользователей
export const getIgnoreList = (profileId) =>
    axiosInstance.get(`/profiles/${profileId}/ignore`);

// Удаление пользователя из списка игнорируемых
export const removeFromIgnore = (profileId, targetId) =>
    axiosInstance.delete(`/profiles/${profileId}/ignore`, { data: { target_id: targetId } });

export default axiosInstance;
